import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Fragment } from "react";

import { Lines } from "@/components/marketing/Lines";
import { BACK_IN, OUT_QUAD, SWING_TO } from "@/components/marketing/easing";
import { BODY_SMALL, HEADING_2 } from "@/components/marketing/marketingType";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Card } from "@/content/landing/types";
import { BoldRuns, LandingButton, LandingImage } from "../LandingPrimitives";
import type { Cards } from "./cardsShared";

/** `.pr-c_feature-icon`: the same tick on every line of every card. */
const TICK = "/marketing/campaign/card-check.svg";

/** `--colors--gray-3`, the card's rule, its price chip and the lines between its points. */
const GRAY_3 = "#E9EEF1";

/** `.pr-c-title_wrap`: the heading and its line, centred on 70% until the wide step. */
const TITLE_WRAP = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  width: "70%",
  mx: "auto",
  textAlign: "center",
  [NOT_WIDE]: { width: 1 },
} satisfies SxProps<Theme>;

/** `.product-card_wrapper`: the gradient panel, dropped on a phone. */
const WRAPPER = {
  display: "flex",
  flexDirection: "column",
  width: 1,
  p: "48px 48px 32px",
  borderRadius: "32px",
  backgroundImage: "linear-gradient(#EDF2F5, #FFFFFF)",
  [NOT_WIDE]: { p: "32px" },
  [TINY]: { p: 0, borderRadius: 0, backgroundImage: "none" },
} satisfies SxProps<Theme>;

/**
 * `.product-card_list`: three to a row, two below the wide step, one below the
 * card step. Plain `1fr` tracks, as published: a card whose price chip will
 * not wrap takes the width it needs from the others.
 */
const LIST = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  alignItems: "stretch",
  gap: "16px",
  [NOT_WIDE]: { gridTemplateColumns: "1fr 1fr" },
  [MID]: { gridTemplateColumns: "1fr" },
} satisfies SxProps<Theme>;

/**
 * `.product-card_item`'s hover, an interaction: the card lifts 2% of its own
 * size up and left with a swing, its rule turns to the accent in a tenth of a
 * second, and it and its price chip change ground at once. It drops back
 * with a draw-back.
 */
const CARD_HOVER = {
  transition: `border-color 100ms ${OUT_QUAD}, transform 275ms ${BACK_IN}`,
  "&:hover": {
    borderColor: "rgba(26, 179, 148, 0.67)",
    backgroundColor: "#E6F7F3",
    transform: "translate(-2%, -2%)",
    transition: `border-color 100ms ${OUT_QUAD}, transform 350ms ${SWING_TO}`,
    "& [data-price-chip]": { backgroundColor: "common.white" },
  },
} satisfies SxProps<Theme>;

/** `.product-card_item`. */
const CARD = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: "32px",
  width: 1,
  p: "32px",
  borderRadius: "16px",
  border: `1px solid ${GRAY_3}`,
  backgroundColor: "common.white",
  boxShadow: "0 20px 34px -10px #E4E9EC",
  color: marketingPalette.ink,
  [NOT_WIDE]: { p: "16px" },
  [TINY]: { gap: "24px", borderColor: "rgba(79, 92, 101, 0.32)" },
} satisfies SxProps<Theme>;

/** `.pr-c-top_title`: the site's `h4`, set in ink. */
const CARD_TITLE = {
  color: marketingPalette.ink,
  fontSize: 28,
  fontWeight: 700,
  lineHeight: "32px",
  letterSpacing: "-0.03em",
  [TINY]: { fontSize: 27, lineHeight: "30px" },
} satisfies SxProps<Theme>;

/** `.pr-c-top_img`: the product's mark, a size per step. */
const MARK = {
  flex: "none",
  width: "96px",
  height: "96px",
  objectFit: "cover",
  [NOT_WIDE]: { width: "80px", height: "80px" },
  [MID]: { width: "112px", height: "112px" },
  [TINY]: { width: "88px", height: "88px" },
} satisfies SxProps<Theme>;

/**
 * `.pr-c_top`: the plan name over the title, beside the mark, the mark over
 * both below the card step, where the two run on one line. Where a page names
 * no plan the published holder keeps its empty `h4`, which draws nothing but
 * still takes the line's 6.4 before the title. The empty box is that gap, not
 * a heading.
 */
function CardTop({ card }: { card: Card }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "4px",
          width: 1,
          [MID]: {
            flexDirection: "column-reverse",
            alignItems: "flex-start",
            gap: "8px",
          },
        }}
      >
        {/* `.pr-c-top_title_holder`: its gap is a column gap, so stacked the two meet. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            columnGap: "4px",
            [MID]: { flexDirection: "row", columnGap: "6.4px" },
          }}
        >
          {card.plan ? (
            <Typography component="h4" sx={CARD_TITLE}>
              {card.plan}
            </Typography>
          ) : (
            <Box aria-hidden />
          )}
          <Typography component="h4" sx={CARD_TITLE}>
            {card.title}
          </Typography>
        </Box>
        {card.icon ? <LandingImage src={card.icon} sx={MARK} /> : null}
      </Box>
      {card.body ? (
        <Box
          sx={{
            fontSize: 14,
            lineHeight: "19.6px",
            textAlign: "left",
            [MID]: { fontSize: 16, lineHeight: "22.4px" },
          }}
        >
          {card.body}
        </Box>
      ) : null}
    </Box>
  );
}

/** `.pr-c-label_sub`. */
const PRICE_UNIT = {
  fontSize: 15,
  lineHeight: "22.5px",
  textAlign: "left",
  [TINY]: { fontSize: 14, lineHeight: "21px" },
} satisfies SxProps<Theme>;

/**
 * `.pr-c_label`: the price, its unit and a note on a grey chip. A page that
 * publishes the note's slot empty still spaces it: `noteSlot`.
 */
function PriceChip({ card, noteSlot }: { card: Card; noteSlot?: boolean }) {
  return (
    <Box
      data-price-chip
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        p: "8px 16px",
        borderRadius: "16px",
        backgroundColor: GRAY_3,
      }}
    >
      <Box sx={{ fontSize: 20, fontWeight: 500, lineHeight: "30px" }}>
        {card.price}
      </Box>
      {card.per ? <Box sx={PRICE_UNIT}>{card.per}</Box> : null}
      {card.priceNote || noteSlot ? (
        <Box sx={PRICE_UNIT}>{card.priceNote}</Box>
      ) : null}
    </Box>
  );
}

/** `.pr-c_feature-list`: the ticked points, a hairline between each two. */
function Points({ lines }: { lines: ReadonlyArray<string> }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "14.08px",
        width: 1,
      }}
    >
      {lines.map((line, index) => (
        <Fragment key={index}>
          {index > 0 ? (
            <Box sx={{ height: "0.96px", backgroundColor: GRAY_3 }} />
          ) : null}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <LandingImage
              src={TICK}
              sx={{ width: "20px", height: "20px", objectFit: "cover" }}
            />
            <Box
              sx={{
                fontSize: 14,
                lineHeight: "22px",
                opacity: 0.85,
                textAlign: "left",
              }}
            >
              <BoldRuns text={line} weight={700} />
            </Box>
          </Box>
        </Fragment>
      ))}
    </Box>
  );
}

function PanelCard({ card, noteSlot }: { card: Card; noteSlot?: boolean }) {
  return (
    <Box sx={[CARD, CARD_HOVER]}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "20px",
          width: 1,
          [NOT_WIDE]: { gap: "16px" },
        }}
      >
        <CardTop card={card} />
        {card.price ? <PriceChip card={card} noteSlot={noteSlot} /> : null}
        {card.bullets?.length ? <Points lines={card.bullets} /> : null}
      </Box>
      {card.cta ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: "4.8px",
          }}
        >
          <LandingButton cta={card.cta} variant="primary" />
        </Box>
      ) : null}
    </Box>
  );
}

/** `.pr-c-title_wrap`: the priced bands' heading and its line. */
export function PricedHeading({
  title,
  body,
}: {
  title?: string;
  body?: string;
}) {
  if (!title) {
    return null;
  }
  return (
    <Box sx={TITLE_WRAP}>
      <Typography component="h2" sx={HEADING_2}>
        <Lines text={title} />
      </Typography>
      {body ? <Typography sx={BODY_SMALL}>{body}</Typography> : null}
    </Box>
  );
}

/** `.section_title-pr`: a priced band's heading over its cards, 32 apart. */
export const PRICED_STACK = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "32px",
} satisfies SxProps<Theme>;

/**
 * `.section_title-pr` over `.product-card_con`: the priced cards on their
 * gradient panel, each a title beside its mark, the price on a grey chip and
 * the ticked points under it.
 */
export function CardPanel({ block }: { block: Cards }) {
  return (
    <Box sx={PRICED_STACK}>
      <PricedHeading title={block.title} body={block.body} />
      <Box sx={WRAPPER}>
        <Box sx={LIST}>
          {block.items.map((card, index) => (
            <PanelCard key={index} card={card} noteSlot={block.noteSlot} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
